'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Screenshot {
  id: number;
  image: string;
  alt: string;
  title: string;
}

const screenshots: Screenshot[] = [
{
  id: 1,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_199ab8631-1767862916066.png",
  alt: 'StoryNest-FM app home screen showing featured story cards with colorful cover art and play buttons',
  title: 'Home Screen'
},
{
  id: 2,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_13e075516-1764669125879.png",
  alt: 'Audio player interface with waveform visualization, playback controls, and story progress bar',
  title: 'Audio Player'
},
{
  id: 3,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d6f83c4c-1767862934825.png",
  alt: 'Story library grid view displaying categorized content with genre filters and search bar',
  title: 'Library'
},
{
  id: 4,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1312ee798-1767862914804.png",
  alt: 'User profile page showing listening statistics, saved playlists, and achievement badges',
  title: 'Profile'
},
{
  id: 5,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_115b3258a-1767862919082.png",
  alt: 'Discover page with personalized recommendations and trending stories carousel',
  title: 'Discover'
}];


export default function AppScreenshots() {
  const [activeIndex, setActiveIndex] = useState(0);

  const handlePrevious = () => {
    setActiveIndex((prev) => prev === 0 ? screenshots.length - 1 : prev - 1);
  };

  const handleNext = () => {
    setActiveIndex((prev) => prev === screenshots.length - 1 ? 0 : prev + 1);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-primary/5 to-accent/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-text-primary mb-4">
            Beautiful Interface,<br />
            <span className="text-primary">Seamless Experience</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Explore the intuitive design that makes discovering and enjoying stories effortless
          </p>
        </div>

        <div className="relative">
          <div className="flex items-center justify-center gap-4 overflow-hidden">
            {screenshots.map((screenshot, index) => {
              const offset = index - activeIndex;
              const isActive = index === activeIndex;

              return (
                <div
                  key={screenshot.id}
                  className={`transition-all duration-500 ${
                  isActive ?
                  'scale-100 opacity-100 z-10' :
                  Math.abs(offset) === 1 ?
                  'scale-75 opacity-50 z-5' : 'scale-50 opacity-0 z-0'}`
                  }
                  style={{
                    transform: `translateX(${offset * 100}%)`,
                    position: isActive ? 'relative' : 'absolute',
                    left: isActive ? 'auto' : '50%',
                    marginLeft: isActive ? '0' : '-150px'
                  }}>

                  <div className="bg-card p-4 rounded-3xl shadow-warm-2xl max-w-xs mx-auto">
                    <AppImage
                      src={screenshot.image}
                      alt={screenshot.alt}
                      className="w-full h-auto rounded-2xl" />

                    {isActive &&
                    <div className="text-center mt-4">
                        <h3 className="text-lg font-heading font-bold text-text-primary">
                          {screenshot.title}
                        </h3>
                      </div>
                    }
                  </div>
                </div>);

            })}
          </div>

          <button
            onClick={handlePrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-card text-text-primary rounded-full shadow-warm-lg hover:bg-primary hover:text-primary-foreground transition-all duration-300 flex items-center justify-center z-20"
            aria-label="Previous screenshot">

            <Icon name="ChevronLeftIcon" size={24} />
          </button>

          <button
            onClick={handleNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-card text-text-primary rounded-full shadow-warm-lg hover:bg-primary hover:text-primary-foreground transition-all duration-300 flex items-center justify-center z-20"
            aria-label="Next screenshot">

            <Icon name="ChevronRightIcon" size={24} />
          </button>

          <div className="flex justify-center mt-8 space-x-2">
            {screenshots.map((_, index) =>
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === activeIndex ?
              'bg-primary w-8' : 'bg-border hover:bg-primary/50'}`
              }
              aria-label={`Go to screenshot ${index + 1}`} />

            )}
          </div>
        </div>
      </div>
    </section>);

}